import { cn } from '../../lib/utils';

export interface TabDefinition<K extends string> {
  key: K;
  label: string;
}

interface TabNavigationProps<K extends string> {
  tabs: ReadonlyArray<TabDefinition<K>>;
  activeTab: K;
  /** Collection sizes shown beside the label so admins can scan before clicking. */
  counts?: Partial<Record<K, number>>;
  /** Prefix for the ids that wire each tab to its `tabpanel`. */
  idPrefix: string;
  ariaLabel: string;
  onTabChange: (tab: K) => void;
}

export function tabPanelId(prefix: string, tab: string) {
  return `${prefix}-tabpanel-${tab}`;
}

export function tabId(prefix: string, tab: string) {
  return `${prefix}-tab-${tab}`;
}

/** Underlined tab strip shared by the admin detail pages. */
export function TabNavigation<K extends string>({
  tabs,
  activeTab,
  counts = {},
  idPrefix,
  ariaLabel,
  onTabChange,
}: TabNavigationProps<K>) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="scrollbar-hide -mb-px flex items-center gap-1 overflow-x-auto"
    >
      {tabs.map(({ key, label }) => {
        const isActive = key === activeTab;
        const count = counts[key];

        return (
          <button
            type="button"
            key={key}
            id={tabId(idPrefix, key)}
            role="tab"
            aria-selected={isActive}
            aria-controls={tabPanelId(idPrefix, key)}
            onClick={() => onTabChange(key)}
            className={cn(
              'relative flex shrink-0 items-center gap-1.5 px-3 py-3 text-[13px] transition-colors',
              isActive
                ? 'font-medium text-white'
                : 'text-neutral-500 hover:text-neutral-200',
            )}
          >
            {label}
            {count !== undefined && count > 0 && (
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[10.5px] tabular-nums transition-colors',
                  isActive
                    ? 'bg-white/10 text-neutral-300'
                    : 'bg-white/[0.04] text-neutral-500',
                )}
              >
                {count}
              </span>
            )}
            {isActive && (
              <span
                aria-hidden="true"
                className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-teal-400"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
